import { KeyboardEvent, MouseEvent as ReactMouseEvent, useEffect, useRef, useState } from "react"
import "./canvas.css"
import {
  passSetRunMode, passKeyboardState,
  passAppleCommandKeyPress, passAppleCommandKeyRelease,
  passGoBackInTime,
  passGoForwardInTime,
  passMouseEvent,
  passPasteText,
  handleGetShowAppleMouse,
  handleGetRunMode,
  handleGetCout,
  passKeyRelease,
  handleGetMachineName,
  handleGetState6502,
  handleGetSoftSwitches,
  handleGetSpeedMode,
  handleGetTextPage,
} from "./main2worker"
import { ARROW, RUN_MODE, convertAppleKey, MouseEventSimple, UI_THEME } from "../common/utility"
import { ProcessDisplay, getCanvasSize, handleGetOverrideHires, canvasCoordToNormScreenCoord, xmargin, ymargin } from "./graphics"
import { checkGamepad, handleArrowKey, handleDiagonalKey, ensureGamepadEventListeners } from "./devices/gamepad"
import { handleCopyToClipboard } from "./copycanvas"
import { handleFileSave } from "./savestate"
import { handleSetCPUState } from "./controller"
import { setPreferenceSpeedMode } from "./localstorage"
import { getLowercaseMode, getShowScanlines, isCanvasOnlyTheme, isMinimalTheme, getTheme, getArrowKeysAsJoystick, isEmbedMode, getUIStateBoolean } from "./ui_settings"
import { KeyboardControl } from "./controls/keyboardcontrol"
import HgrMagnifier from "./hgrmagnifier"
import { useGlobalContext } from "./globalcontext"
import { toggleScanlines } from "./ui_utilities"
import { getMouseButtonReleaseEvents } from "./mouseevent"

let resizeTimer = 0
let resizeFrame = 0
const maxFrameSamples = 60

let currentCommand = ""
const recallBuffer: string[] = []
let recallIndex = 99

type keyEvent = KeyboardEvent<HTMLTextAreaElement> | KeyboardEvent<HTMLCanvasElement>  | KeyboardEvent<HTMLBodyElement> | KeyboardEvent<HTMLButtonElement>
type ArrowKeyCode = "ArrowLeft" | "ArrowRight" | "ArrowUp" | "ArrowDown" | "Numpad4" | "Numpad6" | "Numpad8" | "Numpad2"
let mainCanvas: HTMLCanvasElement | null = null

const Apple2Canvas = (props: DisplayProps) => {
  // const { updateHgr: updateHgr, setUpdateHgr: setUpdateHgr,
  //   hgrMagnifierLoc, setHgrMagnifierLoc: setHgrMagnifierLoc } = useGlobalContext()
  const { lockHgrMagnifier, setLockHgrMagnifier } = useGlobalContext()
  const [keyHandled, setKeyHandled] = useState(false)
  const [magnifierMouseLoc, setMagnifierMouseLoc] = useState([-1, -1])
  const [isCanvasFullScreen, setIsCanvasFullScreen] = useState(false)
  const [withinScreen, setWithinScreen] = useState(false)
  const lastFrameTimeRef = useRef(0)
  const startTimeForMaxFramesRef = useRef(0)
  const lastFPSLogRef = useRef(0)
  // Always points at the newest RenderCanvas closure so the perpetual rAF loop
  // below picks up HMR-updated code instead of running the stale one forever.
  const renderCanvasRef = useRef<(timestamp: number) => void>(() => { })

  const myCanvas = useRef<HTMLCanvasElement>(null)
  const hiddenCanvas = useRef<HTMLCanvasElement>(null)
  const hardwareKeyboardKey = useRef<{ code: string, key: number } | null>(null)
  const didNavigateWithKeyboard = useRef(false)

  const pasteHandler = (e: ClipboardEvent) => {
    const canvas = document.getElementById("apple2canvas")
    if (document.activeElement === canvas && e.clipboardData) {
      const data = e.clipboardData.getData("text")
      if (data !== "") {
        passPasteText(data)
      }
      e.preventDefault()
    }
  }

  const syntheticPaste = () => {
    const canvas = document.getElementById("apple2canvas")
    if (document.activeElement === canvas && document.hasFocus()) {
      // Errors can sometimes occur during debugging, if the canvas loses
      // focus. I tried using a try/catch but it didn't help. Just ignore.
      navigator.clipboard
        .readText()
        .then((data) => passPasteText(data))
    }
  }

  const metaKeyHandlers: Record<string, () => void> = {
    ArrowLeft: () => passGoBackInTime(),
    ArrowRight: () => passGoForwardInTime(),
    b: () => handleSetCPUState(RUN_MODE.NEED_BOOT),
    c: () => handleCopyToClipboard(),
    o: () => props.setShowFileOpenDialog(true, -1),
    r: () => handleSetCPUState(RUN_MODE.NEED_RESET),
    s: () => handleFileSave(false),
    v: () => syntheticPaste(),
    0: () => setPreferenceSpeedMode(-2),
    1: () => setPreferenceSpeedMode(0),
    2: () => setPreferenceSpeedMode(1),
    3: () => setPreferenceSpeedMode(2),
    4: () => setPreferenceSpeedMode(3),
    5: () => setPreferenceSpeedMode(4),
  }

  const handleMetaKey = (key: string) => {
    if (key in metaKeyHandlers) {
      metaKeyHandlers[key]()
      return true
    }
    return false
  }

  const hardwareKeyboardRepeats = () => {
    const machine = handleGetMachineName()
    return machine === "APPLE2EU" || machine === "APPLE2EE"
  }

  const stopHardwareKeyboard = (code?: string) => {
    if (code && hardwareKeyboardKey.current?.code !== code) return
    if (hardwareKeyboardKey.current) {
      passKeyboardState({
        key: hardwareKeyboardKey.current.key,
        isDown: false,
        repeat: false,
      })
    }
    hardwareKeyboardKey.current = null
  }

  const startHardwareKeyboard = (code: string, key: number) => {
    if (key < 0) return
    if (hardwareKeyboardKey.current?.code === code) return
    stopHardwareKeyboard()
    hardwareKeyboardKey.current = { code: code, key }
    passKeyboardState({
      key,
      isDown: true,
      repeat: hardwareKeyboardRepeats(),
      saveSnapShot: getUIStateBoolean("snapshotOnKeyPress"),
    })
  }

  const arrowKeys: Record<ArrowKeyCode, ARROW> = {
    ArrowLeft: ARROW.LEFT,
    ArrowRight: ARROW.RIGHT,
    ArrowUp: ARROW.UP,
    ArrowDown: ARROW.DOWN,
    // Numpad 8/2/4/6 Joystick Mapping (Immune to NumLock)
    Numpad4: ARROW.LEFT,
    Numpad6: ARROW.RIGHT,
    Numpad8: ARROW.UP,
    Numpad2: ARROW.DOWN,
  }

  const arrowKeyCodes: Record<ArrowKeyCode, number> = {
    ArrowLeft: 8,
    ArrowRight: 21,
    ArrowUp: 11,
    ArrowDown: 10,
    Numpad4: 8,
    Numpad6: 21,
    Numpad8: 11,
    Numpad2: 10,
  }

  const diagonalKeys: Record<string, boolean> = {
    Numpad7: true,
    Numpad9: true,
    Numpad1: true,
    Numpad3: true,
  }

  const getArrowKeyName = (e: keyEvent): ArrowKeyCode | null => {
    if (e.code in arrowKeys) return e.code as ArrowKeyCode
    if (e.key in arrowKeys) return e.key as ArrowKeyCode
    return null
  }

  const isMac = navigator.platform.startsWith("Mac")

  // A non-collapsed selection outside the emulator screen means the user has selected
  // text in the surrounding UI - the cycle count, the stack dump, disassembly, and so
  // on. The Apple II screen is a canvas with no text nodes, so any selection like this
  // belongs to the interface rather than the emulator.
  const hasUITextSelection = () => {
    const selection = window.getSelection()
    if (!selection || selection.isCollapsed || selection.rangeCount === 0) return false
    const canvas = document.getElementById("apple2canvas")
    return !canvas || !canvas.contains(selection.getRangeAt(0).commonAncestorContainer)
  }

  // Copy is the one shortcut that genuinely collides: on a Mac Cmd+C is not an Apple II
  // chord, so it is ours to give away, and on Windows Ctrl+C has to stay a real Apple II
  // key - but only when the user is not sitting on a selection somewhere else in the UI.
  const isCopyShortcut = (e: keyEvent) =>
    e.key.toLowerCase() === "c" && (isMac ? e.metaKey : e.ctrlKey)

  const isOpenAppleDown = (e: keyEvent) => {
    const useOpenAppleKey = getUIStateBoolean("useOpenAppleKey")
    return e.code === "AltLeft" || (useOpenAppleKey && e.code === "MetaLeft") || e.code === "Numpad0" || e.code === "Numpad5"
  }

  const isOpenAppleUp = (e: keyEvent) => {
    const useOpenAppleKey = getUIStateBoolean("useOpenAppleKey")
    return e.code === "AltLeft" || (useOpenAppleKey && e.code === "MetaLeft") || e.code === "Numpad0" || e.code === "Numpad5"
  }

  const isClosedAppleDown = (e: keyEvent) => {
    const useOpenAppleKey = getUIStateBoolean("useOpenAppleKey")
    return e.code === "AltRight" || (useOpenAppleKey && e.code === "MetaRight") || e.code === "NumpadDecimal" || e.code === "NumpadEnter"
  }

  const isClosedAppleUp = (e: keyEvent) => {
    const useOpenAppleKey = getUIStateBoolean("useOpenAppleKey")
    return e.code === "AltRight" || (useOpenAppleKey && e.code === "MetaRight") || e.code === "NumpadDecimal" || e.code === "NumpadEnter"
  }

  const isMetaSequence = (e: keyEvent): boolean => {
    if (e.shiftKey) {
      return false
    }
    // ASCII does not allow Ctrl+numbers, so just allow those as metasequence keys.
    // For example, Ctrl+3 to change to warp speed.
    if (e.key >= "0" && e.key <= "9" && e.ctrlKey) {
      return true
    }
    if (getUIStateBoolean("useOpenAppleKey")) {
      return false
    }
    if (isMac) {
      return e.metaKey && e.key !== "Meta"
    } else {
      return e.altKey && e.key !== "Alt"
    }
  }

  const inKeyboardLoop = () => {
    const s6502 = handleGetState6502()
    const switches = handleGetSoftSwitches()
    const isKeyboardLoop = switches.TEXT && s6502.PC >= 0xC26D && s6502.PC <= 0xC28E
    // See if we have a square bracket ], integer basic >, or monitor * prompt,
    // which are the three known keyboard loops. If we're not in one of those,
    // then just treat it as a normal keypress.
    if (!isKeyboardLoop) {
      return false
    }
    const textPage = handleGetTextPage()
    // First split into lines, dependiing upon whether we are 40 or 80 columns
    if (textPage.length > 0) {
      const nBytesPerLine = (textPage.length === 320 || textPage.length === 1920) ? 80 : 40
      for (let i = 0; i < textPage.length; i += nBytesPerLine) {
        const prompt = String.fromCharCode(textPage[i] & 0x7F)
        if (prompt.includes("]") || prompt.includes(">") || prompt.includes("*")) {
          return true
        }
      }
    }
    return false
  }

  const handleKeyDown = (e: keyEvent) => {
    // Let the browser copy the user's own selection instead of the Apple II screen.
    // Returning without preventDefault leaves the native clipboard behaviour intact;
    // bailing out here (rather than inside the meta-key handling) also avoids arming
    // the Open/Closed Apple modifier state for a chord we are not going to send.
    if (isCopyShortcut(e) && hasUITextSelection()) return

    let keyHandledLocal = false
    const isBrowserAltKey = e.code === "AltLeft" || e.code === "AltRight"
    if (isOpenAppleDown(e)) {
      passAppleCommandKeyPress(true)
    }
    if (isClosedAppleDown(e)) {
      passAppleCommandKeyPress(false)
    }
    const isNumpadFireButton = e.code === "Numpad0" || e.code === "Numpad5" || e.code === "NumpadDecimal" || e.code === "NumpadEnter"
    if (getArrowKeysAsJoystick() && isNumpadFireButton) {
      e.preventDefault()
      e.stopPropagation()
      return
    }
    if (isBrowserAltKey) {
      e.preventDefault()
      e.stopPropagation()
    }
    // TODO: What modifier key should be used on Windows? Can't use Ctrl
    // because that interferes with Apple II control keys like Ctrl+S
    //if (!e.shiftKey && (isMac ? (e.metaKey && e.key !== 'Meta') : (e.altKey && e.key !== 'Alt'))) {
    if (isMetaSequence(e)) {
      keyHandledLocal = handleMetaKey(e.key)
      // TODO: This allows Cmd+V to paste text, but breaks OpenApple+V.
      // How to handle both?
      //if (e.key === 'v') return;
    }
    // If we're paused, allow <space> to resume
    if (handleGetRunMode() === RUN_MODE.PAUSED && e.key === " ") {
      passSetRunMode(RUN_MODE.RUNNING)
      keyHandledLocal = true
    }
    if (keyHandledLocal) {
      setKeyHandled(true)
      passAppleCommandKeyRelease(true)
      passAppleCommandKeyRelease(false)
      e.preventDefault()
      e.stopPropagation()
      return
    }

    const isKeyboardLoop = inKeyboardLoop()

    if (e.code in diagonalKeys) {
      if (e.repeat) {
        e.preventDefault()
        e.stopPropagation()
        return
      }
      handleDiagonalKey(e.code, false)
      e.preventDefault()
      e.stopPropagation()
      return
    }

    const arrowKey = getArrowKeyName(e)
    if (arrowKey) {
      if (e.repeat) {
        e.preventDefault()
        e.stopPropagation()
        return
      }

      if (isKeyboardLoop && recallBuffer.length > 0 &&
        (arrowKeys[arrowKey] === ARROW.UP || arrowKeys[arrowKey] === ARROW.DOWN)) {
        if (arrowKeys[arrowKey] === ARROW.UP) {
          recallIndex = Math.max(0, recallIndex - 1)
        } else if (arrowKeys[arrowKey] === ARROW.DOWN) {
          recallIndex = Math.min(recallBuffer.length, recallIndex + 1)
        }
        const recallCommand = recallBuffer[recallIndex]
        if (currentCommand.length > 0) {
          // Clear current command from BASIC line
          // Pass a single string containing the correct number of backspaces,
          // to avoid flooding the message channel with individual key events.
          const back = "\b".repeat(currentCommand.length)
          passPasteText(back + " ".repeat(currentCommand.length) + back)
        }
        if (recallCommand) {
          // Type recalled command into BASIC line
          passPasteText(recallCommand)
          currentCommand = recallCommand
        } else {
          currentCommand = ""
        }
      } else {
        startHardwareKeyboard(e.code, arrowKeyCodes[arrowKey] || 0)
        handleArrowKey(arrowKeys[arrowKey], false)
      }
      e.preventDefault()
      e.stopPropagation()
      return
    }

    const lowercaseMode = getLowercaseMode()
    const key = convertAppleKey(e, lowercaseMode, props.ctrlKeyMode, handleGetCout())
    if (key >= 0) {
      if (e.repeat) {
        e.preventDefault()
        e.stopPropagation()
        return
      }
      startHardwareKeyboard(e.code, key)
      if (isKeyboardLoop) {
        if (key === 13) {
          if (currentCommand.length > 0) {
            if (currentCommand !== recallBuffer[recallBuffer.length - 1]) {
              recallBuffer.push(currentCommand)
              if (recallBuffer.length > 50) {
                recallBuffer.shift()
              }
            }
            currentCommand = ""
            recallIndex = recallBuffer.length
          }
        } else if (key >= 32 && key <= 126) {
          currentCommand += String.fromCharCode(key)
        }
      }
      e.preventDefault()
      e.stopPropagation()
    }
    if (props.ctrlKeyMode == 1) {
      props.handleCtrlDown(0)
    }
    if (props.openAppleKeyMode == 1) {
      props.handleOpenAppleDown(0)
    }
    if (props.closedAppleKeyMode == 1) {
      props.handleClosedAppleDown(0)
    }
  }

  const handleKeyUp = (e: keyEvent) => {
    const isBrowserAltKey = e.code === "AltLeft" || e.code === "AltRight"
    stopHardwareKeyboard(e.code)
    const arrowKey = getArrowKeyName(e)
    if (isOpenAppleUp(e)) {
      passAppleCommandKeyRelease(true)
    }
    if (isClosedAppleUp(e)) {
      passAppleCommandKeyRelease(false)
    }
    const isNumpadFireButton = e.code === "Numpad0" || e.code === "Numpad5" || e.code === "NumpadDecimal" || e.code === "NumpadEnter"
    if (getArrowKeysAsJoystick() && isNumpadFireButton) {
      e.preventDefault()
      e.stopPropagation()
      return
    }
    if (e.code in diagonalKeys) {
      handleDiagonalKey(e.code, true)
    } else if (arrowKey) {
      handleArrowKey(arrowKeys[arrowKey], true)
    }
    if (keyHandled) {
      setKeyHandled(false)
      e.preventDefault()
      e.stopPropagation()
    }
    if (isBrowserAltKey) {
      e.preventDefault()
      e.stopPropagation()
      return
    }
  }

  const releaseBlurredModifierState = () => {
    stopHardwareKeyboard()
    passAppleCommandKeyRelease(true)
    passAppleCommandKeyRelease(false)
    passKeyRelease()
  }

  const handleWindowBlur = () => {
    releaseBlurredModifierState()
  }

  const handleVisibilityChange = () => {
    if (document.hidden) {
      releaseBlurredModifierState()
    }
  }

  const handleResize = () => {
    if (mainCanvas) {
      handleCanvasResize(mainCanvas)
      props.updateDisplay()
    }
  }

  const [width, height] = getCanvasSize()

  const RenderCanvas = (timestamp: number) => {
    const elapsed = timestamp - lastFrameTimeRef.current
    // Some odd "race the beam" graphics demos require 60 FPS, but only do this
    // for 1 MHz or slower speeds. Throttle the frame rate at higher CPU
    // speed to help reduce CPU usage.
    // Test image from Brendan:
    // https://github.com/badvision/a2-pseudocolor/raw/refs/heads/main/disks/flicker.po
    const targetFrameRate = (handleGetSpeedMode() <= 0) ? 75 : 45 // Hz
    const targetInterval = 1000 / targetFrameRate

    if (elapsed >= targetInterval) {
      if (mainCanvas && hiddenCanvas.current) {
        // Note: Do not add { willReadFrequently: true }, as it increased CPU
        // usage by about 20% in Chrome.
        const ctx = (mainCanvas as HTMLCanvasElement).getContext("2d")
        const hiddenCtx = (hiddenCanvas.current as HTMLCanvasElement).getContext("2d")
        if (ctx && hiddenCtx) {
          const [w, h] = getCanvasSize()   // always current
          ProcessDisplay(ctx, hiddenCtx, w, h)
        }
        checkGamepad()
      }

      // Calculate and log FPS
      lastFPSLogRef.current++
      if (lastFPSLogRef.current >= maxFrameSamples) {
        const avgFPS = (lastFPSLogRef.current / (timestamp - startTimeForMaxFramesRef.current)) * 1000
        const fps = avgFPS.toFixed(1)
        props.setAvgFPS(fps)
        lastFPSLogRef.current = 0
        startTimeForMaxFramesRef.current = timestamp
      }

      lastFrameTimeRef.current = timestamp
    }

    // Changing this refresh interval to be less often has no effect on the "fast" speed.
    window.requestAnimationFrame((t) => renderCanvasRef.current(t))
  }
  // Refs must not be mutated during render, so update it after each render instead.
  useEffect(() => {
    renderCanvasRef.current = RenderCanvas
  })

  const scaleMouseEvent = (event: ReactMouseEvent<HTMLCanvasElement>): MouseEventSimple | null => {
    let x = 0
    let y = 0
    if (mainCanvas) {
      [x, y] = canvasCoordToNormScreenCoord(mainCanvas, event.clientX, event.clientY)
      if (x < 0 || x > 1 || y < 0 || y > 1) {
        return null
      }
    }
    return { x, y, buttons: -1 }
  }

  const handleMouseDown = (event: ReactMouseEvent<HTMLCanvasElement>) => {
    const evt = scaleMouseEvent(event)
    if (!evt) return
    evt.buttons = event.button === 0 ? 0x10 : 0x11
    passMouseEvent(evt)
    if (handleGetOverrideHires()) {
      const newLockHgrMagnifier = !lockHgrMagnifier
      setLockHgrMagnifier(newLockHgrMagnifier)
      setMagnifierMouseLoc([event.clientX, event.clientY])
    }
  }

  const handleMouseUp = (event: ReactMouseEvent<HTMLCanvasElement>) => {
    const evt = scaleMouseEvent(event)
    if (!evt) return
    evt.buttons = event.button === 0 ? 0x00 : 0x01
    passMouseEvent(evt)
  }

  const handleMouseMove = (event: ReactMouseEvent<HTMLCanvasElement>) => {
    const evt = scaleMouseEvent(event)
    if (handleGetOverrideHires() && !lockHgrMagnifier) {
      setMagnifierMouseLoc([event.clientX, event.clientY])
    }
    if (!evt) {
      setWithinScreen(false)
      return
    }
    setWithinScreen(true)
    passMouseEvent(evt)
  }

  const handleMouseEnter = (event: ReactMouseEvent<HTMLCanvasElement>) => {
    setFocus()
    for (const release of getMouseButtonReleaseEvents(event.buttons, isTouchDevice)) {
      passMouseEvent(release)
    }
  }

  const handleCanvasResize = (canvas: HTMLCanvasElement | null) => {
    if (!canvas) return "0px"

    window.clearTimeout(resizeTimer)
    window.cancelAnimationFrame(resizeFrame)
    resizeTimer = window.setTimeout(() => {
      resizeFrame = window.requestAnimationFrame(() => {
        resizeFrame = window.requestAnimationFrame(() => {
          const width = canvas.offsetWidth
          const height = canvas.offsetHeight
          let scanlinesLeft = 0
          let scanlinesTop = 0
          const isFullScreen = document.fullscreenElement === canvas.parentElement

          if (!isFullScreen) {
            let marginLeft = canvas.offsetLeft + width * xmargin
            let marginTop = canvas.offsetTop + height * ymargin

            if (isMinimalTheme()) {
              const appleScreenWidth = width //- 2 * width * xmargin //+ 20
              const appleScreenHeight = height //- 2 * height * ymargin
              marginLeft = (window.innerWidth - appleScreenWidth) / 2
              marginTop = Math.min((window.innerHeight - appleScreenHeight) / 2, 75)
              const debugSection = document.getElementsByClassName("flyout-top-right")[0] as HTMLElement
              if (debugSection && debugSection.offsetWidth > 200) {
                marginLeft = Math.max(Math.min(marginLeft, (debugSection.offsetLeft - appleScreenWidth) / 2), 0)
              }
              scanlinesLeft = marginLeft
              scanlinesTop = marginTop
              canvas.style.marginLeft = `${scanlinesLeft}px`
              canvas.style.marginTop = `${scanlinesTop}px`
            } else {
              canvas.style.marginLeft = "0px"
              canvas.style.marginTop = "0px"
              scanlinesLeft = canvas.offsetLeft
              scanlinesTop = canvas.offsetTop
            }
          } else {
            const marginLeft = Math.max((window.innerWidth - width) / 2, 0)
            const marginTop = Math.max((window.innerHeight - height) / 2, 0)
            canvas.style.marginLeft = `${marginLeft}px`
            canvas.style.marginTop = `${marginTop}px`
            scanlinesLeft = 0
            scanlinesTop = 0
          }

          document.body.style.setProperty("--scanlines-left", `${scanlinesLeft}px`)
          document.body.style.setProperty("--scanlines-top", `${scanlinesTop}px`)
          document.body.style.setProperty("--scanlines-width", `${width}px`)
          document.body.style.setProperty("--scanlines-height", `${height}px`)
          document.body.style.setProperty("--scanlines-size", `${height / 192}px`)
        })
      })
    }, 0)

    return canvas.style.marginLeft
  }

  const handleBodyKey = (
    type: "keydown" | "keyup",
    handler: (event: keyEvent) => void
  ) => {
    const listener: EventListener = (event) => {
      const modal = document.getElementsByClassName("modal-overlay")
      const target = event.target as HTMLElement

      if (
        type === "keydown" &&
        target.tagName !== "CANVAS" &&
        (event as unknown as keyEvent).code === "Tab"
      ) {
        didNavigateWithKeyboard.current = true
      }

      if (
        !didNavigateWithKeyboard.current &&
        !modal.length &&
        (target.tagName === "BODY" || target.tagName === "BUTTON" || target.id === "veraCanvas")
      ) {
        handler(event as unknown as keyEvent)
      }
    }

    window.addEventListener(type, listener)

    return listener
  }

  // To make sure this only gets called once, do not add dependencies such as RenderCanvas.
  useEffect(() => {
    mainCanvas = document.getElementById("apple2canvas") as HTMLCanvasElement
    mainCanvas.addEventListener("copy", () => { handleCopyToClipboard() })
    const paste = (e: object) => { pasteHandler(e as ClipboardEvent) }
    mainCanvas.addEventListener("paste", paste)
    window.addEventListener("blur", handleWindowBlur)
    document.addEventListener("visibilitychange", handleVisibilityChange)
    const handleFullscreenChange = () => {
      setIsCanvasFullScreen(document.fullscreenElement === mainCanvas?.parentElement)
      handleResize()
      handleCanvasResize(mainCanvas)
    }
    document.addEventListener("fullscreenchange", handleFullscreenChange)
    ensureGamepadEventListeners()
    handleResize()

    new ResizeObserver(entries => {
      for (const entry of entries) {
        handleCanvasResize(entry.target as HTMLCanvasElement)
      }
    }).observe(mainCanvas)
    // Set initial state here since displayconfig.tsx only updates it on toggle.
    toggleScanlines(getShowScanlines())

    renderCanvasRef.current(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Add listener to handle keys when canvas is not in focus.
  // Remove listeners when hot-reloading.
  useEffect(() => {
    const keyDownListener = handleBodyKey("keydown", handleKeyDown)
    const keyUpListener = handleBodyKey("keyup", handleKeyUp)

    return () => {
      window.removeEventListener("keydown", keyDownListener)
      window.removeEventListener("keyup", keyUpListener)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const setFocus = () => {
    if (mainCanvas) {
      (mainCanvas as HTMLCanvasElement).focus({ preventScroll: true })
    }
  }

  const isTouchDevice = "ontouchstart" in document.documentElement
  const noBackgroundImage = isTouchDevice || isCanvasFullScreen || isCanvasOnlyTheme()

  // useEffect(() => {
  //   if (handleGetOverrideHires() && updateHgr) {
  //     setUpdateHgr(false)  // no need for setTimeout inside an effect
  //     if (hgrMagnifier[0] !== -1 && (hgrMagnifier[0] !== hgrMagnifierLocal[0] || hgrMagnifier[1] !== hgrMagnifierLocal[1])) {
  //       setHgrMagnifierCoord(hgrMagnifier)
  //       setshowHgrMagnifier(true)
  //     }
  //   }
  // // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [updateHgr, hgrMagnifier])

  const cursor = (handleGetShowAppleMouse() && withinScreen) ?
    `url(${window.assetRegistry.dotCursor}), none` : "default"

  const machine = handleGetMachineName()
  const bgImg = machine === "APPLE2P" ?
    window.assetRegistry.bgImgApple2Plus : window.assetRegistry.bgImage
  const backgroundImage = noBackgroundImage ? "" : `url("${new URL(bgImg, document.baseURI).href}")`

  return (
    <span className="canvas-text scanline-gradient"
      style={{ "--monitor-frame-image": backgroundImage,} as React.CSSProperties}>
      <canvas ref={myCanvas}
        id="apple2canvas"
        className="main-canvas"
        style={{
          cursor: cursor,
          borderColor: (machine === "APPLE2P" || getTheme() === UI_THEME.DARK) ? "black" : "#583927",
          borderRadius: noBackgroundImage ? "0" : "18px",
          borderWidth: (noBackgroundImage || machine === "APPLE2P") ? "0" : "2px",
        }}
        width={width} height={height}
        tabIndex={0}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onMouseEnter={handleMouseEnter}
        onMouseDown={isTouchDevice ? setFocus : (e) => { setFocus(); handleMouseDown(e) }}
        onMouseUp={isTouchDevice ? undefined : handleMouseUp}
        onMouseMove={isTouchDevice ? undefined : handleMouseMove}
        onFocus={() => { didNavigateWithKeyboard.current = false }}
      />
      {/* Use hidden canvas/context so image rescaling works in iOS < 15.
          See graphics.ts drawImage() */}
      <canvas ref={hiddenCanvas}
        id="hiddenCanvas"
        hidden={true}
        width={560} height={384} />
      {handleGetOverrideHires() && <HgrMagnifier mainCanvas={mainCanvas}
        mouseLoc={magnifierMouseLoc} lockHgrMagnifier={lockHgrMagnifier} />}
      {!isEmbedMode() && <KeyboardControl />}
    </span>
  )
}

export default Apple2Canvas
